import React, { useEffect, useState, useCallback, useRef } from 'react';
import HeaderLogged from './components/header_logged';
import api from './api/client';
import AdminTabs from './components/AdminTabs';
import JsonDiff, { collectDiffPaths } from './components/JsonDiff';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import './admin-panel.css';

const PAGE_SIZE = 50;
const emptyFilters = { q: '', action: '', actor: '', from: '', to: '', page: '' };

const fmtDate = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Верхнеуровневые изменённые поля записи — для чипсов в списке.
function changedFields(row) {
    const d = row.details;
    if (!d || typeof d !== 'object') return null;
    try {
        if (d.before !== undefined && d.after !== undefined) {
            const diffs = new Set();
            collectDiffPaths(d.before ?? null, d.after ?? null, '', diffs);
            return [...new Set([...diffs].map(p => p.split('.')[0]).filter(Boolean))];
        }
        if (row.action === 'data.create' && d.after && typeof d.after === 'object') {
            return Object.keys(d.after);
        }
        if (row.action === 'data.request' && d.body && typeof d.body === 'object') {
            return Object.keys(d.body);
        }
    } catch {
        return null;
    }
    return null;
}

// Страница /admin/audit (раздел admin-audit — System Admin и Owner):
// журнал изменений с фильтрами и разворотом старого/нового значения.
function AuditLogPage() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [actions, setActions] = useState([]);
    const [users, setUsers] = useState([]);
    const [pages, setPages] = useState([]);
    const [filters, setFilters] = useState(emptyFilters);
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Пермалинк: /admin/audit?id=123 открывает журнал на конкретной записи.
    const [recordId, setRecordId] = useState(() => new URLSearchParams(window.location.search).get('id'));
    const [copiedId, setCopiedId] = useState(null);
    const [exporting, setExporting] = useState(false);

    // Поиск применяется с задержкой, чтобы не дёргать сервер на каждую букву.
    useEffect(() => {
        const t = setTimeout(() => {
            setFilters(f => (f.q === search ? f : { ...f, q: search }));
        }, 400);
        return () => clearTimeout(t);
    }, [search]);

    // Номер последнего запроса: ответы, обогнанные более новым запросом
    // (смена фильтра во время загрузки), молча выбрасываются.
    const seqRef = useRef(0);

    const load = useCallback((offset) => {
        const seq = ++seqRef.current;
        setLoading(true);
        setError(null);
        api.get('/api/admin/audit', { params: { ...filters, id: recordId || undefined, limit: PAGE_SIZE, offset } })
            .then(r => {
                if (seqRef.current !== seq) return;
                setRows(prev => offset === 0 ? r.data.rows : [...prev, ...r.data.rows]);
                setTotal(r.data.total);
                setActions(r.data.actions);
                if (recordId && offset === 0 && r.data.rows.length === 1) {
                    setExpanded(r.data.rows[0].id);
                }
            })
            .catch(err => {
                if (seqRef.current !== seq) return;
                setError(err.response?.data?.error || 'Failed to load audit log');
            })
            .finally(() => {
                if (seqRef.current === seq) setLoading(false);
            });
    }, [filters, recordId]);

    useEffect(() => { load(0); }, [load]);

    useEffect(() => {
        // Для фильтра по сотруднику и подписей entity вида user.* → имя.
        api.get('/api/admin/users')
            .then(r => setUsers(r.data))
            .catch(() => {}); // без списка страница всё равно работает
        api.get('/api/admin/pages')
            .then(r => setPages(r.data))
            .catch(() => {});
    }, []);

    const titleByKey = {};
    for (const p of pages) titleByKey[p.page_key] = p.title;

    const sectionLabel = (row) => {
        const keys = row.details && Array.isArray(row.details.pages) ? row.details.pages : null;
        if (!keys || keys.length === 0) return null;
        const first = titleByKey[keys[0]] || keys[0];
        return keys.length > 1 ? `${first} +${keys.length - 1}` : first;
    };

    const applyPreset = (fromDaysAgo, toDaysAgo) => {
        const from = new Date();
        from.setDate(from.getDate() - fromDaysAgo);
        const to = new Date();
        to.setDate(to.getDate() - toDaysAgo);
        setFilters(prev => ({ ...prev, from: fmtDate(from), to: fmtDate(to) }));
    };

    const clearRecordMode = () => {
        setRecordId(null);
        window.history.replaceState(null, '', window.location.pathname);
    };

    const showHistory = (entity) => {
        clearRecordMode();
        setSearch(entity);
        setFilters({ ...emptyFilters, q: entity });
        setExpanded(null);
    };

    const copyLink = (rowId) => {
        const url = `${window.location.origin}/admin/audit?id=${rowId}`;
        const done = () => {
            setCopiedId(rowId);
            setTimeout(() => setCopiedId(null), 1500);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(done).catch(() => window.prompt('Copy link:', url));
        } else {
            window.prompt('Copy link:', url);
        }
    };

    // Экспорт текущей выборки (до 1000 строк) в CSV для Excel.
    const exportCsv = async () => {
        setExporting(true);
        setError(null);
        try {
            const all = [];
            for (let offset = 0; offset < 1000; offset += 200) {
                const r = await api.get('/api/admin/audit', {
                    params: { ...filters, id: recordId || undefined, limit: 200, offset },
                });
                all.push(...r.data.rows);
                if (r.data.rows.length < 200) break;
            }
            const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
            const lines = [['id', 'when', 'who', 'action', 'sections', 'target', 'changed', 'details'].join(',')];
            for (const row of all) {
                lines.push([
                    row.id,
                    new Date(row.at).toLocaleString(),
                    row.actor_name || (row.actor_id ? `#${row.actor_id}` : 'system'),
                    row.action,
                    (row.details && Array.isArray(row.details.pages) ? row.details.pages : []).join('; '),
                    row.entity || '',
                    (changedFields(row) || []).join('; '),
                    row.details ? JSON.stringify(row.details) : '',
                ].map(esc).join(','));
            }
            // BOM — чтобы Excel корректно открыл UTF-8.
            const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `audit-${fmtDate(new Date())}.csv`;
            a.click();
            URL.revokeObjectURL(a.href);
            if (all.length >= 1000) {
                window.alert('Exported the first 1000 records — narrow the filters for a full export.');
            }
        } catch (e) {
            setError('Export failed');
        } finally {
            setExporting(false);
        }
    };

    const userName = (id) => {
        const u = users.find(x => String(x.employeeID) === String(id));
        return u ? `${u.employeeName} ${u.employeeSurname || ''}`.trim() : null;
    };

    const entityLabel = (row) => {
        if (row.entity == null) return '—';
        if (row.action.startsWith('user.')) return userName(row.entity) || `#${row.entity}`;
        return row.entity;
    };

    const setFilter = (key, value) => {
        if (recordId) clearRecordMode(); // смена фильтра выводит из режима одной записи
        setFilters(f => ({ ...f, [key]: value }));
    };

    return (
        <>
            <HeaderLogged />
            <div className="roles-page">
                <div className="roles-page__head">
                    <h2>Audit Log</h2>
                    <span className="admin-panel__muted">
                        {total === null ? `${rows.length}+ record(s)` : `${total} record(s)`}
                    </span>
                </div>

                <AdminTabs />

                {error && (
                    <div className="admin-panel__error">
                        <FontAwesomeIcon icon={faCircleExclamation} /> {error}
                    </div>
                )}

                {recordId && (
                    <div className="audit-record-banner">
                        <span>Showing single record #{recordId} (shared link)</span>
                        <button type="button" className="admin-panel__grant-btn audit-filters__clear"
                            onClick={clearRecordMode}>
                            Show all records
                        </button>
                    </div>
                )}

                <div className="admin-panel__inline-form audit-filters">
                    <input
                        type="text"
                        className="audit-filters__search"
                        placeholder="Search: item, name, value…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    <select value={filters.action} onChange={e => setFilter('action', e.target.value)}>
                        <option value="">All actions</option>
                        {actions.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <select value={filters.actor} onChange={e => setFilter('actor', e.target.value)}>
                        <option value="">All users</option>
                        {users.filter(u => u.hasAccount).map(u => (
                            <option key={u.employeeID} value={u.employeeID}>
                                {u.employeeName} {u.employeeSurname || ''}
                            </option>
                        ))}
                    </select>
                    <select value={filters.page} onChange={e => setFilter('page', e.target.value)}>
                        <option value="">All sections</option>
                        {pages.map(p => <option key={p.page_key} value={p.page_key}>{p.title}</option>)}
                    </select>
                    <input type="date" value={filters.from} onChange={e => setFilter('from', e.target.value)} />
                    <input type="date" value={filters.to} onChange={e => setFilter('to', e.target.value)} />
                    <span className="audit-filters__presets">
                        <button type="button" onClick={() => applyPreset(0, 0)}>Today</button>
                        <button type="button" onClick={() => applyPreset(1, 1)}>Yesterday</button>
                        <button type="button" onClick={() => applyPreset(6, 0)}>7d</button>
                    </span>
                    <button type="button" className="admin-panel__grant-btn audit-filters__clear"
                        disabled={exporting} onClick={exportCsv}>
                        {exporting ? 'Exporting…' : 'Export CSV'}
                    </button>
                    {(filters.q || filters.action || filters.actor || filters.from || filters.to) && (
                        <button type="button" className="admin-panel__grant-btn audit-filters__clear"
                            onClick={() => { setSearch(''); setFilters(emptyFilters); }}>
                            Clear
                        </button>
                    )}
                </div>

                <div className="roles-page__grid-wrap">
                    <table className="admin-panel__table audit-table">
                        <thead>
                            <tr>
                                <th className="audit-table__toggle-col" />
                                <th>When</th>
                                <th>Who</th>
                                <th>Action</th>
                                <th>Section</th>
                                <th>Target</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => (
                                <React.Fragment key={row.id}>
                                    <tr className={row.details ? 'audit-table__row is-expandable' : 'audit-table__row'}
                                        onClick={() => row.details && setExpanded(expanded === row.id ? null : row.id)}>
                                        <td className="audit-table__toggle-col">
                                            {row.details && (
                                                <FontAwesomeIcon icon={expanded === row.id ? faChevronDown : faChevronRight} />
                                            )}
                                        </td>
                                        <td className="audit-table__when">{new Date(row.at).toLocaleString()}</td>
                                        <td>{row.actor_name || (row.actor_id ? `#${row.actor_id}` : 'system')}</td>
                                        <td><span className="audit-table__action">{row.action}</span></td>
                                        <td className="audit-table__section">{sectionLabel(row) || <span className="admin-panel__muted">—</span>}</td>
                                        <td>
                                            {entityLabel(row)}
                                            {(() => {
                                                const fields = changedFields(row);
                                                if (!fields || fields.length === 0) return null;
                                                const shown = fields.slice(0, 4);
                                                return (
                                                    <span className="audit-chips">
                                                        {shown.map(f => <span key={f} className="audit-chips__chip">{f}</span>)}
                                                        {fields.length > 4 && (
                                                            <span className="audit-chips__chip audit-chips__chip--more">+{fields.length - 4}</span>
                                                        )}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                    </tr>
                                    {expanded === row.id && row.details && (
                                        <tr className="admin-panel__sub-row">
                                            <td colSpan={6}>
                                                <div className="audit-details__toolbar">
                                                    {row.entity && row.entity.includes('#') && (
                                                        <button type="button" className="admin-panel__grant-btn audit-filters__clear"
                                                            onClick={() => showHistory(row.entity)}>
                                                            History of this object
                                                        </button>
                                                    )}
                                                    <button type="button" className="admin-panel__grant-btn audit-filters__clear"
                                                        onClick={() => copyLink(row.id)}>
                                                        {copiedId === row.id ? 'Copied!' : 'Copy link'}
                                                    </button>
                                                </div>
                                                <div className="audit-details">
                                                    {row.details.before !== undefined && row.details.after !== undefined ? (
                                                        <JsonDiff before={row.details.before} after={row.details.after} />
                                                    ) : row.details.before !== undefined || row.details.after !== undefined ? (
                                                        <>
                                                            <div className="audit-details__col">
                                                                <div className="audit-details__label">Before</div>
                                                                <pre>{JSON.stringify(row.details.before ?? null, null, 2)}</pre>
                                                            </div>
                                                            <div className="audit-details__col">
                                                                <div className="audit-details__label">After</div>
                                                                <pre>{JSON.stringify(row.details.after ?? null, null, 2)}</pre>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="audit-details__col">
                                                            <pre>{JSON.stringify(row.details, null, 2)}</pre>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                            {!loading && rows.length === 0 && (
                                <tr><td colSpan={6} className="admin-panel__muted">No records</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {(total === null
                    ? rows.length > 0 && rows.length % PAGE_SIZE === 0
                    : rows.length < total) && (
                    <div className="audit-more">
                        <button type="button" className="admin-panel__grant-btn" disabled={loading}
                            onClick={() => load(rows.length)}>
                            {loading ? 'Loading…'
                                : total === null ? 'Load more' : `Load more (${total - rows.length} left)`}
                        </button>
                    </div>
                )}
            </div>
        </>
    );
}

export default AuditLogPage;
